import React, { useState } from "react";
import handleFormChange from "helpers/handle-form-change";
import { validatePhoneNumber } from "helpers/validations";
import Input from "components/input";
import ErrorMessage from "components/error-message";
import useToggle from "hooks/useToggle";
import clsx from "clsx/lite";
import axios from "axios";
import PurchasedProducts from "./purchasedProducts";
import Loader from "components/loader";
import Image from "components/image";

const initialUserFormState = { firstName: "", lastName: "", email: "", phoneNumber: "" };
const initialOrderFormState = { orderId: "", orderDate: "", productTitle: "", file: null, declinesMarketing: false };


const WarrantyActivationForm = ({ data }) => {
	const [loading, toggleLoading] = useToggle();
	const [userFormState, setUserFormState] = useState(initialUserFormState);
	const [orderFormState, setOrderFormState] = useState(initialOrderFormState);

	const [selectedProducts, setSelectedProducts] = useState([]);
	const [errors, setErrors] = useState({});

	const [showSecondForm, setShowSecondForm] = useState(false);
	const [success, setSuccess] = useState(false);

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		setErrors({ ...errors, file: null }); // Reset file error

		if (file && file.size > 5 * 1024 * 1024) { // 5MB
			setErrors({ ...errors, file: "File size should not exceed 5MB." });
		} else {
			setOrderFormState({ ...orderFormState, file });
		}
	};

	const showNextForm = async (e) => {
		e.preventDefault();
		toggleLoading();
		if (!showSecondForm && !errors.phoneNumber) {
			setErrors({});
			setShowSecondForm(true);
		}
		toggleLoading();
	};

	const formSubmit = async (e) => {
		e.preventDefault();
		setErrors({});

		// Validate required fields
		if (selectedProducts.length === 0) {
			setErrors((prev) => ({ ...prev, products: "Please select at least one product." }));
			return;
		}

		toggleLoading();

		try {
			const formData = getFormData();

			const fileData = await getFileData(orderFormState.file);

			const response = await sendFormData(formData, fileData);

			setSuccess(true);
			return response.data;
		} catch (error) {
			console.error("Error in form submission", error);
			setSuccess(true);
			setErrors({ form: "An error occurred while submitting the form. Please try again later." });
			return null;
		} finally {
			toggleLoading();
		}
	};

	const getFormData = () => ({
		id: orderFormState.orderId,
		date: orderFormState.orderDate,
		purchasedProducts: selectedProducts.join(", "),
		firstName: userFormState.firstName,
		lastName: userFormState.lastName,
		email: userFormState.email,
		phoneNumber: userFormState.phoneNumber,
		declinesMarketing: orderFormState.declinesMarketing,
	});

	const getFileData = async (file) => {
		if (file && file instanceof Blob) {
			const base64File = await readFileAsBase64(file);
			return {
				base64: base64File.split(",")[1],
				name: file.name,
				type: file.type,
				size: file.size,
			};
		}
		return null;
	};

	const sendFormData = (orderDetails, fileData) => {
		return axios.post(
			"https://modlighting-func.netlify.app/.netlify/functions/amazon-form",
			{ orderDetails, file: fileData },
			{ headers: { "Content-Type": "application/json" } }
		);
	};

	const readFileAsBase64 = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result);
			reader.onerror = reject;
			reader.readAsDataURL(file);
		});
	};

	return (
		<div style={{ backgroundImage: `url(${data.backgroundImage})` }} className="relative w-full flex">
			<div className="w-full h-full flex-center sm500:my-10">
				<div className="w-full sm:w-[500px] max-w-[500px] bg-white p-10">
					{success ? (
						<div className="flex-center w-full h-full text-center">
							<div className="bg-white p-10 flex flex-col gap-5">
								<h2 className="text-center">Thank You</h2>
								<p>We have received your submission. Thanks again for shopping with MOD LIGHTING.</p>
							</div>
						</div>
					) : (
						<>
							<div className="w-full flex justify-center mb-5">
								<Image
									image={data.logo}
									className="w-32 h-14 mx-auto" />
							</div>
							<h3 className="text-center">{data.title}</h3>
							{errors?.form && (
								<ErrorMessage message={errors?.form} className="justify-center w-4/5 mx-auto" />
							)}
							<form
								className={clsx("mt-8 flex-col gap-6", showSecondForm ? "hidden" : "flex")}
								onSubmit={showNextForm}
								onChange={(e) => handleFormChange(e, setUserFormState)}
							>
								<Input
									error={errors?.firstName} name="firstName" label="First Name"
									value={userFormState.firstName} autoFocus placeholder="John" required
								/>
								<Input
									error={errors?.lastName} name="lastName" label="Last Name"
									value={userFormState.lastName} placeholder="Doe" required
								/>
								<Input
									error={errors?.email} type="email" name="email" label="Email"
									value={userFormState.email} placeholder="user@gmail.com" required
								/>
								<Input
									error={errors?.phoneNumber} name="phoneNumber" label="Phone Number"
									type="tel"
									onChange={(e) => validatePhoneNumber(e, setErrors)}
									value={userFormState.phoneNumber}
									placeholder="Phone Number"
								/>

								<div className="flex flex-col items-center justify-center">
									<button disabled={loading} type="submit" className="pt-5 btn-primary w-full">
										Next
									</button>
								</div>
							</form>
							<form
								className={clsx("mt-8 flex-col gap-6", showSecondForm ? "flex" : "hidden")}
								onSubmit={formSubmit}
								onChange={(e) => handleFormChange(e, setOrderFormState)}
							>
								<Input
									error={errors?.orderId} name="orderId" label="Order Id"
									value={orderFormState.orderId} autoFocus
									placeholder="Order ID" required
								/>
								<Input
									error={errors?.orderDate} name="orderDate" label="Purchase Date"
									value={orderFormState.orderDate} type="date"
									required max={new Date().toISOString().split("T")[0]}
									className="date-input"
								/>
								<PurchasedProducts
									products={data.products}
									selectedProducts={selectedProducts}
									setSelectedProducts={setSelectedProducts}
									orderFormState={orderFormState}
									setOrderFormState={setOrderFormState}
									errors={errors}
									setErrors={setErrors}
								/>
								<Input
									error={errors?.file} type="file" name="file" label="Upload File"
									accept="image/*, application/pdf" onChange={handleFileChange}
								/>
								<div className="flex items-center gap-4">
									<input type="checkbox" name="declinesMarketing"
										defaultChecked={orderFormState.declinesMarketing}
									/>
									<p>Opt out of all email updates and exclusive promos.</p>
								</div>
								<div className="flex flex-col items-center justify-center">
									<button disabled={loading} type="submit" className="pt-5 btn-primary w-full">
										{loading ? <Loader className="size-5" ring /> : <span>Submit</span>}
									</button>
								</div>
							</form>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default WarrantyActivationForm;
